import "bootstrap/dist/css/bootstrap.min.css";
import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import SuccessModal from "../../components/modals/SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";
import CountryCodeSelect from "../../components/CountryCodeSelect";
import { FONTS_CONFIG, getFontOptions, loadGoogleFonts } from "../../config/fonts";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function GeneralSettings() {
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [formData, setFormData] = useState({
    whatsapp_enabled: false,
    whatsapp_phone: "",
    whatsapp_country_code: "+",
    notification_enabled: false,
    notification_email: "",
    cod_enabled: false,
    card_enabled: true,
    company_font_key: "montserrat",
    company_font_size: "24",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { userData } = useContext(AuthContext);

  const company_code = userData?.company_code;

  useEffect(() => {
    async function fetchGeneralSettings() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${BASE_URL}/api/admin/website/get-support/${company_code}`
        );
        if (!res.ok) throw new Error("Failed to fetch website settings");
        const data = await res.json();
        const settings = data.settings || {};
        setFormData({
          whatsapp_enabled: settings.whatsapp_enabled || false,
          whatsapp_phone: String(settings.whatsapp_phone || ""),
          whatsapp_country_code: settings.whatsapp_country_code || "+",
          notification_enabled: settings.notification_enabled || false,
          notification_email: settings.notification_email || "",
          cod_enabled: !!settings.cod_enabled,
          card_enabled: settings.card_enabled === undefined ? true : !!settings.card_enabled,
          company_font_key: settings.company_font_key || "montserrat",
          company_font_size: String(settings.company_font_size || "24"),
        });
        // Load the saved font for preview
        if (settings.company_font_key && FONTS_CONFIG[settings.company_font_key]?.googleFont) {
          loadGoogleFonts([settings.company_font_key]);
        }
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    if (company_code) {
      fetchGeneralSettings();
    }
  }, [company_code]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Load the Google Font when user changes the font selection
    if (name === "company_font_key" && FONTS_CONFIG[value]?.googleFont) {
      loadGoogleFonts([value]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanWhatsAppPhone = formData.whatsapp_phone
        ? formData.whatsapp_phone.replace(/\D/g, "")
        : "";

      const requestData = {
        whatsapp_enabled: formData.whatsapp_enabled,
        whatsapp_country_code: formData.whatsapp_country_code,
        whatsapp_phone: cleanWhatsAppPhone,
        notification_enabled: formData.notification_enabled,
        notification_email: (formData.notification_email || "").trim(),
        cod_enabled: formData.cod_enabled ? 1 : 0,
        card_enabled: formData.card_enabled ? 1 : 0,
        company_font_key: formData.company_font_key,
        company_font_size: parseInt(formData.company_font_size, 10) || 24,
      };

      const res = await fetch(
        `${BASE_URL}/api/admin/website/add-support/${company_code}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!res.ok) throw new Error("Update failed");
      showSuccess("Website settings updated successfully!");
    } catch (err) {
      showError(err.message || "Failed to update website settings");
    }
  };

  return (
    <Container fluid>
      <Card className="register-card">
        <Card.Body>
          <div className="category-header-section">
            <h1>General Settings</h1>
          </div>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="secondary" />
            </div>
          ) : (
            <Form className="register-form" onSubmit={handleSubmit}>
              <Row className="border-bottom pb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Name Font</Form.Label>
                    <Form.Select
                      name="company_font_key"
                      value={formData.company_font_key}
                      onChange={handleChange}
                      className="mb-2"
                    >
                      {getFontOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted d-block">
                      Select the font style for your company name displayed on
                      the website
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Name Font Size</Form.Label>
                    <Form.Select
                      name="company_font_size"
                      value={formData.company_font_size}
                      onChange={handleChange}
                      className="mb-2"
                    >
                      {[18, 20, 22, 24, 26, 28, 32, 36].map((size) => (
                        <option key={size} value={String(size)}>
                          {size}px
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted d-block">
                      Choose the font size for your company name preview
                    </Form.Text>
                    {/* Live preview of selected font and size */}
                    <div
                      className="mt-2 p-2 border rounded"
                      style={{
                        fontFamily:
                          FONTS_CONFIG[formData.company_font_key]?.css ||
                          "'Montserrat', sans-serif",
                        fontSize: `${formData.company_font_size}px`,
                        fontWeight: "600",
                      }}
                    >
                      Your Company Name
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="pt-4">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="Enable WhatsApp Button for Customer Service"
                        checked={formData.whatsapp_enabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsapp_enabled: e.target.checked,
                          }))
                        }
                        className="d-inline-block me-2"
                      />
                    </Form.Label>
                    <Form.Text className="text-muted d-block">
                      Enable this to allow customers to contact you via WhatsApp
                      from your website
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="border-bottom pb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>WhatsApp Phone Number</Form.Label>
                    <div className="d-flex gap-2">
                      <CountryCodeSelect
                        value={formData.whatsapp_country_code}
                        onChange={(code) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsapp_country_code: code,
                          }))
                        }
                        placeholder="Code"
                        isRequired={formData.whatsapp_enabled}
                        width="100px"
                        disabled={!formData.whatsapp_enabled}
                      />
                      <Form.Control
                        type="text"
                        name="whatsapp_phone"
                        value={formData.whatsapp_phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            whatsapp_phone: value,
                          }));
                        }}
                        placeholder="Enter WhatsApp number"
                        required={formData.whatsapp_enabled}
                        disabled={!formData.whatsapp_enabled}
                        style={{ flex: 1 }}
                      />
                    </div>
                    <Form.Text className="text-muted d-block mt-2">
                      Customers will be able to contact you directly via
                      WhatsApp using this number
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="pt-4">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="Send Notification Email to Admin on New Order"
                        checked={formData.notification_enabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            notification_enabled: e.target.checked,
                          }))
                        }
                        className="d-inline-block me-2"
                      />
                    </Form.Label>
                    <Form.Text className="text-muted d-block">
                      Enable this to send a notification email to the admin on
                      new orders
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="border-bottom pb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notification Email Address</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        name="notification_email"
                        value={formData.notification_email}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            notification_email: value,
                          }));
                        }}
                        placeholder="Enter notification email"
                        required={formData.notification_enabled}
                        disabled={!formData.notification_enabled}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="pt-4">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="Allow Cash on Delivery Payment Method"
                        checked={formData.cod_enabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            cod_enabled: e.target.checked,
                          }))
                        }
                        className="d-inline-block me-2"
                      />
                    </Form.Label>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="Allow Card Payment Method"
                        checked={formData.card_enabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            card_enabled: e.target.checked,
                          }))
                        }
                        className="d-inline-block me-2"
                      />
                    </Form.Label>
                  </Form.Group>
                </Col>
              </Row>

              <div className="text-center">
                <Button
                  type="submit"
                  className="register-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating Settings...
                    </>
                  ) : (
                    "Save Settings"
                  )}
                </Button>
              </div>

              {error && (
                <Alert variant="danger" className="mt-3 text-center">
                  {error}
                </Alert>
              )}
            </Form>
          )}
        </Card.Body>
      </Card>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        variant="success"
        modalTitle={successTitle}
        message={successMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        variant="error"
        modalTitle={errorTitle}
        message={errorMessage}
      />
    </Container>
  );
}
